import { useState, useEffect } from 'react';
import { entityDiscoveryService } from '../services/entity-discovery.service';
import { EntityMetadata } from '../services/types/entity-discovery.types';

export interface DynamicEntityInfo {
  name: string;
  resourceName: string;
  label: string;
  metadata: EntityMetadata;
}

export const useDynamicEntities = () => {
  const [entities, setEntities] = useState<DynamicEntityInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadEntities();
  }, []);

  const loadEntities = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const discovery = await entityDiscoveryService.discoverEntities();
      
      // Filter entities that have REST controllers and convert to resource format
      const availableEntities = discovery.entities
        .filter(entity => entity.hasRestController)
        .map(entity => {
          const resourceName = generateResourceName(entity.name);
          const label = formatEntityLabel(entity.name) || resourceName || 'Unknown';
          
          console.log(`[useDynamicEntities] Entity: ${entity.name} -> Label: "${label}"`);
          
          return {
            name: entity.name,
            resourceName,
            label,
            metadata: entity
          };
        })
        // Sort alphabetically by label
        .sort((a, b) => a.label.localeCompare(b.label));
      
      setEntities(availableEntities);
      console.log('Loaded dynamic entities for menu:', availableEntities.map(e => e.label));
    } catch (err) {
      console.error('Failed to load dynamic entities:', err);
      setError(err instanceof Error ? err.message : 'Failed to load entities');
    } finally {
      setLoading(false);
    }
  };

  const refreshEntities = async () => {
    await loadEntities();
  };

  return {
    entities,
    loading,
    error,
    refreshEntities
  };
};

/**
 * Generate resource name for React Admin (converts PascalCase to lowercase)
 */
function generateResourceName(entityName: string): string {
  return entityName.toLowerCase();
}

/**
 * Format entity name for display in menu
 * Handles acronyms properly (AI, UI, etc.)
 */
function formatEntityLabel(entityName: string): string {
  // Convert PascalCase to space-separated words while preserving acronyms
  // AIAssistant -> AI Assistant
  // UIFile -> UI File
  // ApplicationDeployment -> Application Deployment
  return entityName
    // Add space before uppercase letter that's followed by lowercase (marks word boundaries)
    .replace(/([A-Z])([a-z])/g, ' $1$2')
    // Add space before uppercase letter that follows a lowercase letter
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    // Capitalize each word (in case they got lowercased)
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
